// A page of the document: its canvas, the clickable areas of its text and
// images, selection, dragging and the rectangle select.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { EngineClient } from '../../engine/worker/client.ts';
import type { FieldWidget, ImageInfo, LineInfo, ObjectActionInfo, ObjectRef, SpanInfo } from '../../engine/worker/protocol.ts';
import type { Quad } from '../../engine/edit/text-edit.ts';
import { renderPage, type PdfjsDocument } from '../pdfjs.ts';
import { clearViewport, crossPageTransform, screenRotation, setViewport } from '../viewports.ts';
import { cssFontFor, DEBUG_OVERLAY, RENDER_MARGIN, RENDER_MARGIN_PX, sameItem, toggled, wordAt, type Editing, type Item, type Selection } from '../model.ts';
import { ImageArea } from './ImageArea.tsx';
import { FieldArea } from './FieldArea.tsx';
import { pageKind, UNCERTAIN, type PageKind } from '../ocr.ts';
import { scanImage } from '../../ocr/kind.ts';
import { SelectionToolbar } from './SelectionToolbar.tsx';

/** The selection bar's size (CSS px), to place it where it covers no other text. */
const BAR_W = 420;
const BAR_H = 40;
/** Room above a selection for the turn grip and its stem (CSS px). */
const GRIP_ROOM = 24;

/** How an object is marked (styles.css): selected (AC-UI-2), or while a rectangle is dragged, what it would select and what it cannot (AC-SEL-1). */
type Mark = 'mark' | 'mark is-selected' | 'mark is-preview' | 'mark is-preview-locked';

export function Page(props: {
  index: number;
  size: { width: number; height: number };
  doc: PdfjsDocument;
  engine?: EngineClient;
  editing?: Editing;
  setEditing: (e?: Editing) => void;
  onSubmit: () => void;
  selection?: Selection;
  setSelection: (s?: Selection) => void;
  onObject: (sel: Selection, action: ObjectActionInfo) => void;
  /** replace an image by a PNG or JPEG file (M9) */
  onReplace: (sel: Selection, file: File) => void;
  /** fill in a form field (M8) */
  onFill: (f: FieldWidget, value: string | string[] | boolean) => void;
  /** a picture for a signature field (D-65) */
  onSign: (f: FieldWidget, file: File) => void;
  /** copy the selection to the system clipboard (AC-CLIP-1) */
  onCopy: (sel: Selection) => void;
  /** move the selection to another page through T (its user space → that page's) (AC-XPAGE-1) */
  onObjectToPage: (sel: Selection, to: number, T: number[]) => void;
  /** turn the selection through T (user space), e.g. a rotation around its centre (AC-ROT-1) */
  onTransform: (sel: Selection, T: number[], degrees: number, resize?: number) => void;
  /** a short notice for the message bar */
  onNote: (text: string) => void;
  /** objects to select once the page's objects are read (after a move) */
  reselect?: ObjectRef[];
  onReselect: (sel?: Selection) => void;
  disabled: boolean;
  /** View mode (D-74): select and copy only; no line editor, move, turn, resize or delete */
  readOnly: boolean;
  /** called once the page is drawn and its text has been read (or there is no engine) */
  onReady?: () => void;
  /** CSS px per PDF point (the zoom, AC-UX-7) */
  scale: number;
  /** debug mode (D-45): objects locked only by a cautious refusal can be chosen */
  debug: boolean;
  /** counts up when recognised words changed: the page reads its text again */
  textVersion: number;
  /** what Enhance can do with the page, once its text has been read; recognised: it has recognised words */
  onKind: (kind: PageKind, recognised: boolean) => void;
  /** Find's matches on this page (M12, AC-FIND-2): marked on screen only; the current one stronger and scrolled to */
  found?: { quads: Quad[]; current: boolean }[];
  /** the current match's number among all pages: when it changes, the current mark is scrolled into view */
  foundCurrent?: number;
}) {
  const section = useRef<HTMLElement>(null);
  const [near, setNear] = useState(false);
  const [rendered, setRendered] = useState(false);
  /** number of text spans the engine returned (undefined until it answered) */
  const [spanCount, setSpanCount] = useState<number>();
  const ready = near && rendered && (spanCount !== undefined || !props.engine);
  useEffect(() => {
    if (ready) props.onReady?.();
  }, [ready]);

  // Virtualization: only pages near the viewport have a canvas. A page shown near the window starts drawing at once,
  // without waiting a frame or two for the observer's first report (which held up the first page by ~10 ms).
  useLayoutEffect(() => {
    const r = section.current!.getBoundingClientRect();
    const m = RENDER_MARGIN_PX;
    if (r.bottom >= -m && r.top <= window.innerHeight + m && r.right >= -m && r.left <= window.innerWidth + m) setNear(true);
  }, []);
  useEffect(() => {
    const el = section.current!;
    const io = new IntersectionObserver(
      (entries) => {
        const isNear = entries[entries.length - 1].isIntersecting;
        setNear(isNear);
        if (!isNear) setRendered(false);
      },
      { rootMargin: RENDER_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={section}
      data-testid={`page-${props.index}`}
      data-rendered={near && rendered ? 'true' : undefined}
      data-spans={near ? spanCount : undefined}
      className="page"
      style={{ width: props.size.width, height: props.size.height }}
    >
      {near ? <RenderedPage {...props} onRendered={() => setRendered(true)} onRenderStart={() => setRendered(false)} onSpans={setSpanCount} /> : null}
    </section>
  );
}

function RenderedPage(props: {
  index: number;
  size: { width: number; height: number };
  doc: PdfjsDocument;
  engine?: EngineClient;
  editing?: Editing;
  setEditing: (e?: Editing) => void;
  onSubmit: () => void;
  selection?: Selection;
  setSelection: (s?: Selection) => void;
  onObject: (sel: Selection, action: ObjectActionInfo) => void;
  /** replace an image by a PNG or JPEG file (M9) */
  onReplace: (sel: Selection, file: File) => void;
  /** fill in a form field (M8) */
  onFill: (f: FieldWidget, value: string | string[] | boolean) => void;
  /** a picture for a signature field (D-65) */
  onSign: (f: FieldWidget, file: File) => void;
  /** copy the selection to the system clipboard (AC-CLIP-1) */
  onCopy: (sel: Selection) => void;
  /** move the selection to another page through T (its user space → that page's) (AC-XPAGE-1) */
  onObjectToPage: (sel: Selection, to: number, T: number[]) => void;
  /** turn the selection through T (user space), e.g. a rotation around its centre (AC-ROT-1) */
  onTransform: (sel: Selection, T: number[], degrees: number, resize?: number) => void;
  onNote: (text: string) => void;
  /** objects to select once the page's objects are read (after a move) */
  reselect?: ObjectRef[];
  onReselect: (sel?: Selection) => void;
  disabled: boolean;
  /** View mode (D-74): select and copy only; no line editor, move, turn, resize or delete */
  readOnly: boolean;
  scale: number;
  debug: boolean;
  textVersion: number;
  onKind: (kind: PageKind, recognised: boolean) => void;
  found?: { quads: Quad[]; current: boolean }[];
  foundCurrent?: number;
  onRendered: () => void;
  onRenderStart?: () => void;
  onSpans: (n: number) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [toCss, setToCss] = useState<(x: number, y: number) => [number, number]>();
  const [toUser, setToUser] = useState<(x: number, y: number) => [number, number]>();
  const [spans, setSpans] = useState<SpanInfo[]>([]);
  const [lines, setLines] = useState<LineInfo[]>([]);
  const [images, setImages] = useState<ImageInfo[]>([]);
  /** the form's widgets on this page (M8) */
  const [fields, setFields] = useState<FieldWidget[]>([]);
  /** a drag of the selected object in progress: the pointer's start and current offset (CSS px) */
  const [drag, setDrag] = useState<{ x0: number; y0: number; dx: number; dy: number }>();
  const [spanError, setSpanError] = useState<string>();

  // Drawing. The first time straight into the page's canvas; again when the zoom changes (AC-UX-7) into a canvas of
  // its own, copied over when done, so the page never shows blank meanwhile (the old picture is stretched until then).
  const drawn = useRef(false);
  /** this page's viewport transform, shared for drags that end on another page */
  const viewport = useRef<number[]>(undefined);
  useEffect(
    () => () => {
      if (viewport.current) clearViewport(props.index, viewport.current);
    },
    [],
  );
  useEffect(() => {
    let alive = true;
    const c = canvas.current!;
    const off = drawn.current ? document.createElement('canvas') : c;
    c.style.width = `${props.size.width}px`;
    c.style.height = `${props.size.height}px`;
    props.onRenderStart?.();
    const render = renderPage(props.doc, props.index + 1, off, props.scale);
    render.done.then(
      (vp) => {
        if (!alive) return;
        if (off !== c) {
          c.width = off.width;
          c.height = off.height;
          c.style.width = off.style.width;
          c.style.height = off.style.height;
          c.getContext('2d')!.drawImage(off, 0, 0);
          off.width = 0;
          off.height = 0;
        }
        drawn.current = true;
        setViewport(props.index, vp.transform);
        viewport.current = vp.transform;
        setToCss(() => (x: number, y: number) => vp.convertToViewportPoint(x, y) as [number, number]);
        setToUser(() => (x: number, y: number) => vp.convertToPdfPoint(x, y) as [number, number]);
        performance.mark(`flexpdf:rendered:${props.index}`);
        props.onRendered();
      },
      (e: Error) => alive && setSpanError(`this page could not be rendered: ${e.message}`),
    );
    return () => {
      alive = false;
      // A render still running holds its canvas: stop it, so the page can be drawn again at once.
      render.cancel();
      if (off !== c) {
        off.width = 0;
        off.height = 0;
      }
    };
  }, [props.doc, props.index, props.scale]);
  // Release the canvas memory when the page leaves the render window.
  useEffect(
    () => () => {
      const c = canvas.current;
      if (c) {
        c.width = 0;
        c.height = 0;
      }
    },
    [],
  );

  useEffect(() => {
    let alive = true;
    props.engine?.call({ type: 'spans', pageIndex: props.index }).then(
      (res) => {
        if (!alive) return;
        // In debug mode a lock that is only cautious does not hold: the change's refusal then offers "Do it anyway" (D-45).
        const r = props.debug
          ? {
              ...res,
              spans: res.spans.map((x) => (x.lockForceable ? { ...x, locked: undefined, editLocked: x.editLocked === x.locked ? undefined : x.editLocked } : x)),
              images: res.images.map((x) => (x.lockForceable ? { ...x, locked: undefined } : x)),
            }
          : res;
        setSpans(r.spans);
        setLines(r.lines);
        setImages(r.images);
        performance.mark(`flexpdf:text:${props.index}`);
        props.onSpans(r.spans.length);
        props.onKind(pageKind(r.spans, r.images, (props.size.width * props.size.height) / props.scale ** 2), r.spans.some((x) => x.ocr));
        // Just moved: the objects stay selected where they are now.
        if (props.reselect) {
          const items: Item[] = [];
          for (const ref of props.reselect) {
            if (ref.kind === 'image') {
              const image = r.images[ref.imageIndex];
              if (image && !image.locked) items.push({ kind: 'image', image });
              continue;
            }
            const line = r.lines[r.spans[ref.spanIndex]?.line];
            const pieces = line?.spans.map((i) => r.spans[i]) ?? [];
            if (line && !pieces.some((x) => x.locked) && !items.some((x) => x.kind === 'span' && x.line.index === line.index)) items.push({ kind: 'span', line, pieces });
          }
          props.onReselect(items.length ? { page: props.index, items } : undefined);
        }
      },
      (e: Error) => alive && setSpanError(`this page's text could not be read for editing: ${e.message}`),
    );
    return () => {
      alive = false;
    };
  }, [props.doc, props.index, props.engine, props.debug, props.textVersion]);

  // The form's fields on this page (M8, AC-ACRO-3). A failure leaves the page without fields, as without a form.
  useEffect(() => {
    let alive = true;
    props.engine?.call({ type: 'fields', pageIndex: props.index }).then(
      (res) => alive && setFields(res.fields),
      () => alive && setFields([]),
    );
    return () => {
      alive = false;
    };
  }, [props.doc, props.index, props.engine, props.textVersion]);

  /** A widget's rectangle in CSS pixels (the page's rotation and zoom applied). */
  const fieldBox = (f: FieldWidget) => {
    const [x0, y0, x1, y1] = f.rect;
    const pts = [toCss!(x0, y0), toCss!(x1, y0), toCss!(x1, y1), toCss!(x0, y1)];
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return { left: Math.min(...xs), top: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
  };

  const boxOf = (s: SpanInfo) => {
    const pts = s.quads.flat().map(([x, y]) => toCss!(x, y));
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return { left: Math.min(...xs), top: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
  };

  const boxOfAll = (ss: SpanInfo[]) => {
    const bs = ss.map(boxOf);
    const left = Math.min(...bs.map((b) => b.left));
    const top = Math.min(...bs.map((b) => b.top));
    return { left, top, width: Math.max(...bs.map((b) => b.left + b.width)) - left, height: Math.max(...bs.map((b) => b.top + b.height)) - top };
  };

  /**
   * The span's clickable area as the glyphs lie (AC-CS-5): one oriented
   * rectangle per run of touching glyph boxes (a word, or a line whose spaces
   * are glyphs), from the run's first top-left corner along the baseline to its
   * last glyph's end. Rotated text gets rotated areas, and the gaps between
   * words are not part of the span, so text drawn across them stays clickable.
   */
  const segmentsOf = (s: SpanInfo): React.CSSProperties[] => {
    // Glyph quads: bottom-left, bottom-right, top-right, top-left (user space). Runs are found in user
    // space (0.5 CSS px = 0.5 / scale pt), so only three corners per run are converted.
    const q = s.quads;
    const out: React.CSSProperties[] = [];
    let first = 0;
    for (let k = 1; k <= q.length; k++) {
      if (k < q.length) {
        const p = q[k - 1];
        const dx = p[1][0] - p[0][0];
        const dy = p[1][1] - p[0][1];
        const len = Math.hypot(dx, dy) || 1;
        // Gap along the baseline from the previous box's end (boxes may overlap: a bbox can start left of the origin).
        const gap = ((q[k][0][0] - p[1][0]) * dx + (q[k][0][1] - p[1][1]) * dy) / len;
        const off = Math.abs((q[k][0][0] - p[0][0]) * dy - (q[k][0][1] - p[0][1]) * dx) / len;
        if (gap <= 0.5 / props.scale && off <= 0.5 / props.scale) continue;
      }
      out.push(areaOf(q[first], q[k - 1]));
      first = k;
    }
    return out;
  };

  /** The oriented rectangle from the first box's top-left corner, along the baseline to the last box's end. */
  const areaOf = (a: [number, number][], b: [number, number][]): React.CSSProperties => {
    const o = toCss!(a[3][0], a[3][1]);
    const end = toCss!(a[3][0] + b[1][0] - a[0][0], a[3][1] + b[1][1] - a[0][1]);
    const bottom = toCss!(a[0][0], a[0][1]);
    const u = [end[0] - o[0], end[1] - o[1]];
    const v = [bottom[0] - o[0], bottom[1] - o[1]];
    const w = Math.hypot(u[0], u[1]);
    const h = Math.hypot(v[0], v[1]);
    // Axis-aligned text (nearly all of it) needs no transform, which costs page memory per element.
    if (w >= 1e-3 && h >= 1e-3 && Math.abs(u[1]) < 1e-6 * w && Math.abs(v[0]) < 1e-6 * h) {
      return { left: Math.min(o[0], end[0]), top: Math.min(o[1], bottom[1]), width: w, height: h };
    }
    if (w < 1e-3 || h < 1e-3) {
      const pts = [...a, ...b].map(([x, y]) => toCss!(x, y));
      const xs = pts.map((p) => p[0]);
      const ys = pts.map((p) => p[1]);
      return { left: Math.min(...xs), top: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
    }
    return { left: o[0], top: o[1], width: w, height: h, transformOrigin: '0 0', transform: `matrix(${u[0] / w}, ${u[1] / w}, ${v[0] / h}, ${v[1] / h}, 0, 0)` };
  };

  /** Top-left of the selected objects' box (CSS px), for the toolbar. */
  /**
   * Where the selection's bar goes (CSS px, its top-left): above the selection, unless it would cover other text or
   * images there and below covers less (a bar over the line above would keep it from being clicked).
   */
  const toolbarAt = (sel: Selection): [number, number] => {
    const pts = sel.items.flatMap((it) => (it.kind === 'span' ? it.pieces.flatMap((x) => x.quads.flat()) : it.image.quad)).map(([x, y]) => toCss!(x, y));
    const top = Math.min(...pts.map((p) => p[1]));
    const bottom = Math.max(...pts.map((p) => p[1]));
    const left = Math.max(0, Math.min(Math.min(...pts.map((p) => p[0])), props.size.width - BAR_W));
    const above = Math.max(0, top - BAR_H - 8 - GRIP_ROOM);
    const below = Math.min(props.size.height - BAR_H, bottom + 8);
    const covered = (t: number) => {
      // A glyph box's edge (the font's full height) grazing the bar does not count.
      const hit = (b: { left: number; right: number; top: number; bottom: number }) => b.left < left + BAR_W && b.right > left && b.top + 4 < t + BAR_H && b.bottom - 4 > t;
      const lineHits = lines.filter((l, k) => !selectedSpans.has(l.spans[0]) && hitBoxes?.lines[k].some(hit)).length;
      const imageHits = images.filter((im, k) => !selectedImages.has(im.index) && hitBoxes && hit(hitBoxes.images[k])).length;
      return lineHits + imageHits;
    };
    const up = covered(above);
    return [left, up && covered(below) < up ? below : above];
  };

  /** The page's selected lines and images (by index), for marking them. */
  const mine = props.selection?.page === props.index ? props.selection.items : [];
  const selectedSpans = new Set(mine.flatMap((it) => (it.kind === 'span' ? it.line.spans : [])));
  const selectedImages = new Set(mine.flatMap((it) => (it.kind === 'image' ? [it.image.index] : [])));
  const lineItem = (s: SpanInfo): Item | undefined => (lines[s.line] ? { kind: 'span', line: lines[s.line], pieces: lines[s.line].spans.map((i) => spans[i]) } : undefined);
  /** Double-click: edit the line in place (AC-UX-3), with the word under the pointer selected. */
  const editLine = (s: SpanInfo, e: React.MouseEvent) => {
    const line = lines[s.line];
    if (props.disabled || props.readOnly || !line || e.shiftKey) return;
    const pieces = line.spans.map((i) => spans[i]);
    if (pieces.every((x) => x.editLocked)) {
      props.onNote(`This text cannot be edited: ${pieces[0].editLocked}.`);
      return;
    }
    const [x, y] = onPage(e);
    const box = (q: [number, number][]) => {
      const css = q.map(([px, py]) => toCss!(px, py));
      return { l: Math.min(...css.map((p) => p[0])), r: Math.max(...css.map((p) => p[0])), t: Math.min(...css.map((p) => p[1])), b: Math.max(...css.map((p) => p[1])) };
    };
    const glyph = s.quads.findIndex((q) => {
      const b = box(q);
      return x >= b.l && x <= b.r && y >= b.t && y <= b.b;
    });
    const select = glyph >= 0 ? wordAt(line, pieces, line.spans.indexOf(s.index), glyph) : undefined;
    props.setSelection(undefined);
    props.setEditing({ page: props.index, line, span: pieces[0], text: line.text, select });
  };

  /** A click selects the object; Shift+click adds it to the selection or takes it out (AC-SEL-1). */
  const pick = (item: Item | undefined, e: React.MouseEvent) => {
    if (drag || afterMarquee.current || !item) return;
    props.setSelection(e.shiftKey ? toggled(props.selection, props.index, item) : { page: props.index, items: [item] });
  };

  /** The pointer in page CSS px (not the window: the page may scroll during a drag). */
  const onPage = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = canvas.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  /** Dragging the selected objects: their areas follow the pointer; on release the move goes to the engine. */
  const startDrag = (e: React.PointerEvent) => {
    if (props.disabled || props.readOnly || e.button !== 0 || e.shiftKey) return;
    e.preventDefault();
    const [x0, y0] = onPage(e);
    setDrag({ x0, y0, dx: 0, dy: 0 });
  };
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const [x, y] = onPage(e);
      setDrag((d) => d && { ...d, dx: x - d.x0, dy: y - d.y0 });
    };
    const up = (e: PointerEvent) => {
      const [x, y] = onPage(e);
      const dx = x - drag.x0;
      const dy = y - drag.y0;
      // Let the click that follows the release pass before the drag state clears.
      setTimeout(() => setDrag(undefined), 0);
      const sel = props.selection;
      if (!sel || Math.hypot(dx, dy) < 3 || !toUser) return;
      // Released over another page: the objects move there (AC-XPAGE-1), landing under the pointer.
      const over = document.elementsFromPoint(e.clientX, e.clientY).find((el) => el instanceof HTMLElement && /^page-\d+$/.test(el.dataset.testid ?? '')) as HTMLElement | undefined;
      if (!over) {
        props.onNote('The objects were dropped outside any page, so nothing was moved.');
        return;
      }
      const to = Number(over.dataset.testid!.slice(5));
      if (to !== props.index) {
        const canvasTo = document.querySelector<HTMLElement>(`[data-testid="canvas-${to}"]`)?.getBoundingClientRect();
        const T = canvasTo && crossPageTransform(props.index, to, [e.clientX - canvasTo.left - drag.x0, e.clientY - canvasTo.top - drag.y0]);
        if (T) props.onObjectToPage(sel, to, T);
        else props.onNote('That page is not shown yet; scroll it into view and try again.');
        return;
      }
      const [ax, ay] = toUser(0, 0);
      const [bx, by] = toUser(dx, dy);
      props.onObject(sel, { kind: 'move', dx: bx - ax, dy: by - ay });
    };
    // A pointer the browser cancels (a touch taken for scrolling, a lost window) ends the drag without moving anything.
    const cancel = () => setDrag(undefined);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [drag !== undefined]);

  /** The selection's box on the page (CSS px): [left, top, right, bottom]. */
  const selBox = (sel: Selection): [number, number, number, number] => {
    const pts = sel.items.flatMap((it) => (it.kind === 'span' ? it.pieces.flatMap((x) => x.quads.flat()) : it.image.quad)).map(([x, y]) => toCss!(x, y));
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  };
  /** The selection's centre on the page (CSS px): what a turn turns around. */
  const centreOf = (sel: Selection): [number, number] => {
    const [l, t, r, b] = selBox(sel);
    return [(l + r) / 2, (t + b) / 2];
  };
  /** Turns the selection `degrees` clockwise as seen around its centre (AC-ROT-1). */
  const rotate = (sel: Selection, degrees: number) => {
    const [cx, cy] = centreOf(sel);
    const T = screenRotation(props.index, degrees, cx, cy);
    if (T) props.onTransform(sel, T, degrees);
  };
  /** A turn being dragged with the round handle: the centre (CSS px), the pointer's starting angle and the turn so far (degrees). */
  const [turning, setTurning] = useState<{ cx: number; cy: number; a0: number; deg: number }>();
  const startRotate = (e: React.PointerEvent) => {
    if (props.disabled || props.readOnly || e.button !== 0 || !props.selection) return;
    e.preventDefault();
    const [cx, cy] = centreOf(props.selection);
    const [x, y] = onPage(e);
    setTurning({ cx, cy, a0: Math.atan2(y - cy, x - cx), deg: 0 });
  };
  useEffect(() => {
    if (!turning) return;
    const angle = (e: PointerEvent) => {
      const [x, y] = onPage(e);
      let deg = ((Math.atan2(y - turning.cy, x - turning.cx) - turning.a0) * 180) / Math.PI;
      deg = ((deg + 540) % 360) - 180;
      // 15° steps unless Shift is held.
      return e.shiftKey ? deg : Math.round(deg / 15) * 15;
    };
    const move = (e: PointerEvent) => setTurning((t) => t && { ...t, deg: angle(e) });
    const up = (e: PointerEvent) => {
      const deg = angle(e);
      setTimeout(() => setTurning(undefined), 0);
      const sel = props.selection;
      if (!sel || Math.abs(deg) < 0.5) return;
      const T = screenRotation(props.index, deg, turning.cx, turning.cy);
      if (T) props.onTransform(sel, T, deg);
    };
    const cancel = () => setTurning(undefined);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [turning !== undefined]);

  // Resizing a single image by a corner handle (AC-IMG-1): scaled about the opposite corner, the aspect ratio kept
  // unless Shift is held. Worked out in user space, so a turned page or a turned image needs nothing special.
  const lone = props.selection?.page === props.index && props.selection.items.length === 1 && props.selection.items[0].kind === 'image' ? props.selection.items[0].image : undefined;
  const [resizing, setResizing] = useState<{ corner: number; T?: number[]; pct?: number }>();
  /** The transform taking the image's corner `k` to the pointer (user space), and its new size in %. */
  const resizeTo = (q: [number, number][], k: number, p: [number, number], free: boolean): { T: number[]; pct: number } | undefined => {
    const A = q[(k + 2) % 4];
    const U = [q[(k + 1) % 4][0] - A[0], q[(k + 1) % 4][1] - A[1]];
    const V = [q[(k + 3) % 4][0] - A[0], q[(k + 3) % 4][1] - A[1]];
    const P = [p[0] - A[0], p[1] - A[1]];
    const det = U[0] * V[1] - U[1] * V[0];
    if (Math.abs(det) < 1e-9) return undefined;
    let su = (P[0] * V[1] - P[1] * V[0]) / det;
    let sv = (U[0] * P[1] - U[1] * P[0]) / det;
    if (!free) {
      const C = [U[0] + V[0], U[1] + V[1]];
      su = sv = (P[0] * C[0] + P[1] * C[1]) / (C[0] * C[0] + C[1] * C[1]);
    }
    if (su < 0.05 || sv < 0.05) return undefined;
    // M = B diag(su, sv) B⁻¹ with B = [U V]; PDF order [a b c d e f]: x' = a x + c y + e, y' = b x + d y + f.
    const inv = [V[1] / det, -V[0] / det, -U[1] / det, U[0] / det];
    const B = [U[0] * su, V[0] * sv, U[1] * su, V[1] * sv];
    const m00 = B[0] * inv[0] + B[1] * inv[2];
    const m01 = B[0] * inv[1] + B[1] * inv[3];
    const m10 = B[2] * inv[0] + B[3] * inv[2];
    const m11 = B[2] * inv[1] + B[3] * inv[3];
    return { T: [m00, m10, m01, m11, A[0] - (m00 * A[0] + m01 * A[1]), A[1] - (m10 * A[0] + m11 * A[1])], pct: Math.round(Math.sqrt(su * sv) * 100) };
  };
  useEffect(() => {
    if (!resizing || !lone || !toUser) return;
    const at = (e: PointerEvent) => resizeTo(lone.quad, resizing.corner, toUser(...onPage(e)), e.shiftKey);
    const move = (e: PointerEvent) => setResizing((r) => r && { ...r, ...at(e) });
    const up = (e: PointerEvent) => {
      const r = at(e);
      setTimeout(() => setResizing(undefined), 0);
      if (r && props.selection && Math.abs(r.pct - 100) >= 0.5) props.onTransform(props.selection, r.T, 0, r.pct);
    };
    const cancel = () => setResizing(undefined);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [resizing !== undefined]);
  /** A selected line with a piece that cannot be changed: it cannot be turned either. */
  const selLocked = mine.some((it) => it.kind === 'span' && it.pieces.some((x) => x.locked));
  /**
   * What turns the selection (AC-ROT-1): a grip on a stem above its centre (also by keyboard: arrows turn 15°, with
   * Shift 90°), and a zone just outside each corner (a lone image: its own corners, outside its resize dots).
   */
  const turnGrips = (sel: Selection) => {
    const [l, t, r, b] = selBox(sel);
    const [cx, cy] = [(l + r) / 2, (t + b) / 2];
    const gy = Math.max(6, t - GRIP_ROOM + 6);
    const corners = lone ? lone.quad.map(([x, y]) => toCss!(x, y)) : [[l, t], [r, t], [r, b], [l, b]];
    return (
      <>
        <span className="rotate-stem" style={{ left: cx, top: gy, height: Math.max(0, t - gy) }} />
        <button
          type="button"
          data-testid="rotate-grip"
          className="rotate-grip"
          style={{ left: cx, top: gy }}
          aria-label="Turn (drag, or arrow keys: 15°, with Shift 90°)"
          title="Drag to turn (15° steps; hold Shift for any angle). Arrow keys: 15°, with Shift 90°"
          onPointerDown={(e) => {
            e.stopPropagation();
            startRotate(e);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            e.preventDefault();
            rotate(sel, (e.key === 'ArrowLeft' ? -1 : 1) * (e.shiftKey ? 90 : 15));
          }}
        />
        {corners.map(([x, y], k) => {
          const d = Math.hypot(x - cx, y - cy) || 1;
          return (
            <span
              key={`t${k}`}
              data-testid={`rotate-corner-${k}`}
              className="rotate-corner"
              title="Drag to turn"
              style={{ left: x + ((x - cx) / d) * 12, top: y + ((y - cy) / d) * 12 }}
              onPointerDown={(e) => {
                e.stopPropagation();
                startRotate(e);
              }}
            />
          );
        })}
      </>
    );
  };
  // Find's current match: scrolled into the middle of the window when it changes (AC-FIND-2).
  const currentMark = useRef<SVGRectElement>(null);
  useEffect(() => {
    currentMark.current?.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [props.foundCurrent, !!toCss, !!props.found?.some((f) => f.current)]);
  /** How a selected object is shown while being dragged or turned. */
  const dragStyle = (selected: boolean): React.CSSProperties =>
    !selected ? {} : drag ? { transform: `translate(${drag.dx}px, ${drag.dy}px)`, zIndex: 20 } : turning ? { transform: `rotate(${turning.deg}deg)`, transformOrigin: `${turning.cx}px ${turning.cy}px`, zIndex: 20 } : {};

  /**
   * A rectangle dragged over the page (CSS px): on release, what it touches is selected (AC-SEL-1). One started on an
   * image (not selected) leaves that image out: it is the background of what is selected, e.g. the words on a scan.
   */
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number; add: boolean; on?: number }>();
  /** a rectangle was just dragged: the click that follows its release (on an image under it) selects nothing */
  const afterMarquee = useRef(false);
  const startMarquee = (e: React.PointerEvent, on?: number) => {
    // Cancelling the pointer's default action (no text selection while dragging) also keeps the focus where it is:
    // a line being edited must still lose it, which applies the edit (AC-UX-3).
    if (document.activeElement instanceof HTMLElement && document.activeElement.closest('.line-editor')) document.activeElement.blur();
    if (props.disabled || e.button !== 0 || !toCss) return;
    e.preventDefault();
    const [x, y] = onPage(e);
    setMarquee({ x0: x, y0: y, x1: x, y1: y, add: e.shiftKey, on });
  };
  /** Each line's ink (its glyphs that are not spaces) and each image, as CSS-px rectangles, for hit-testing the rectangle. */
  const hitBoxes = useMemo(() => {
    if (!toCss) return undefined;
    const rect = (pts: [number, number][]) => {
      const css = pts.map(([px, py]) => toCss(px, py));
      const xs = css.map((p) => p[0]);
      const ys = css.map((p) => p[1]);
      return { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };
    };
    return {
      // (An invisible layer that recognised words stand in for is not shown, so it cannot be hit.)
      lines: lines.map((l) => l.spans.flatMap((i) => (spans[i].hidden ? [] : spans[i].quads.flatMap((q, k) => (spans[i].blanks[k] ? [] : [rect(q)]))))),
      images: images.map((im) => rect(im.quad)),
    };
  }, [toCss, lines, spans, images]);
  type Box = { left: number; right: number; top: number; bottom: number };
  // A scanned page with text over it (recognised words, or the scanner's hidden text; Bug2 follow-up): its picture is
  // the background of the words. A click on it goes to the nearest line within half a line's height, else ends the
  // selection; a double-click selects the picture itself. A scan with no text is selected by a click as before.
  const pageArea = (props.size.width * props.size.height) / props.scale ** 2;
  const scan = spans.some((x) => x.ocr || x.invisible) && pageKind(spans, images, pageArea) !== 'none' ? scanImage(images, pageArea) : undefined;
  const clickBackground = (e: React.MouseEvent) => {
    if (drag || afterMarquee.current) return;
    const [x, y] = onPage(e);
    let best: { k: number; d: number } | undefined;
    hitBoxes?.lines.forEach((boxes, k) => {
      for (const b of boxes) {
        const d = Math.hypot(Math.max(b.left - x, 0, x - b.right), Math.max(b.top - y, 0, y - b.bottom));
        if (d <= (b.bottom - b.top) / 2 && (!best || d < best.d)) best = { k, d };
      }
    });
    const near = best && lines[best.k] ? lineItem(spans[lines[best.k].spans[0]]) : undefined;
    if (near?.kind === 'span' && !near.pieces.every((x) => x.locked)) pick(near, e);
    else if (!e.shiftKey) props.setSelection(undefined);
  };
  /** The lines and images the rectangle touches: those it selects, and those that cannot be changed. */
  const touchedBy = (r: Box, on?: number): { found: Item[]; locked: Item[] } => {
    const found: Item[] = [];
    const locked: Item[] = [];
    if (!hitBoxes) return { found, locked };
    const hit = (b: Box) => b.left <= r.right && b.right >= r.left && b.top <= r.bottom && b.bottom >= r.top;
    for (const [k, l] of lines.entries()) {
      if (!hitBoxes.lines[k].some(hit)) continue;
      const pieces = l.spans.map((i) => spans[i]);
      (pieces.some((x) => x.locked) ? locked : found).push({ kind: 'span', line: l, pieces });
    }
    for (const [k, im] of images.entries()) if (im.index !== on && hit(hitBoxes.images[k])) (im.locked ? locked : found).push({ kind: 'image', image: im });
    return { found, locked };
  };
  const marqueeBox = (m: { x0: number; y0: number; x1: number; y1: number }): Box => ({ left: Math.min(m.x0, m.x1), right: Math.max(m.x0, m.x1), top: Math.min(m.y0, m.y1), bottom: Math.max(m.y0, m.y1) });
  /** Shown while the rectangle is dragged (not for a click). */
  const preview = marquee && (Math.abs(marquee.x1 - marquee.x0) >= 3 || Math.abs(marquee.y1 - marquee.y0) >= 3) ? touchedBy(marqueeBox(marquee), marquee.on) : undefined;
  const previewSpans = new Map<number, 'found' | 'locked'>();
  const previewImages = new Map<number, 'found' | 'locked'>();
  for (const kind of ['found', 'locked'] as const) {
    for (const it of preview?.[kind] ?? []) {
      if (it.kind === 'span') it.line.spans.forEach((i) => previewSpans.set(i, kind));
      else previewImages.set(it.image.index, kind);
    }
  }
  const markOf = (selected: boolean, previewed?: 'found' | 'locked'): Mark => (previewed === 'locked' ? 'mark is-preview-locked' : selected ? 'mark is-selected' : previewed ? 'mark is-preview' : 'mark');

  useEffect(() => {
    if (!marquee) return;
    const move = (e: PointerEvent) => {
      const [x, y] = onPage(e);
      setMarquee((r) => r && { ...r, x1: x, y1: y });
    };
    const up = (e: PointerEvent) => {
      const [x, y] = onPage(e);
      const r = marqueeBox({ ...marquee, x1: x, y1: y });
      setMarquee(undefined);
      // A click on the empty page (no drag) clears the selection.
      if (r.right - r.left < 3 && r.bottom - r.top < 3) {
        if (!marquee.add) props.setSelection(undefined);
        return;
      }
      afterMarquee.current = true;
      setTimeout(() => (afterMarquee.current = false), 0);
      // A line is touched when the ink of any of its glyphs is (not a space); objects that cannot be changed are left out.
      const { found, locked: lockedItems } = touchedBy(r, marquee.on);
      const locked = lockedItems.length;
      let next: Selection | undefined = marquee.add && props.selection?.page === props.index ? props.selection : undefined;
      for (const it of found) if (!next?.items.some((x) => sameItem(x, it))) next = { page: props.index, items: [...(next?.items ?? []), it] };
      props.setSelection(next);
      if (locked) props.onNote(`${locked} object${locked === 1 ? '' : 's'} in the marked area cannot be changed and ${locked === 1 ? 'was' : 'were'} not selected.`);
    };
    // A touch that the browser takes for scrolling ends the rectangle without selecting.
    const cancel = () => setMarquee(undefined);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
    window.addEventListener('pointercancel', cancel, { once: true });
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [marquee !== undefined]);

  /** Glyph boxes in CSS pixels, each [x0, y0, … x3, y3, blank]: for tests and debugging (AC-CS-5, AC-OPEN-4). */
  const glyphsOf = (s: SpanInfo) =>
    JSON.stringify(s.quads.map((q, k) => [...q.flatMap(([x, y]) => toCss!(x, y).map((v) => Math.round(v * 10) / 10)), s.blanks[k] ? 1 : 0]));

  return (
    <>
      <canvas ref={canvas} data-testid={`canvas-${props.index}`} onPointerDown={startMarquee} className="page-canvas" />
      {marquee && (
        <div
          data-testid="marquee"
          className="marquee"
          style={{ left: Math.min(marquee.x0, marquee.x1), top: Math.min(marquee.y0, marquee.y1), width: Math.abs(marquee.x1 - marquee.x0), height: Math.abs(marquee.y1 - marquee.y0) }}
        />
      )}
      {spanError && (
        <p data-testid={`page-${props.index}-error`} className="page-error">
          {spanError}
        </p>
      )}
      {toCss && images.map((im) => <ImageArea key={`i${im.index}`} background={im === scan ? { onClick: clickBackground } : undefined} page={props.index} image={im} area={areaOf(im.quad, im.quad)} selected={selectedImages.has(im.index)} mark={markOf(selectedImages.has(im.index), previewImages.get(im.index))} hover={!marquee} previewed={previewImages.get(im.index)} disabled={props.disabled} drag={drag} startDrag={startDrag} startMarquee={(e) => startMarquee(e, im.index)} motion={dragStyle(selectedImages.has(im.index))} onPick={(e) => pick({ kind: 'image', image: im }, e)} />)}
      {toCss &&
        spans.map((s) => {
          // The line being edited: one input over its first piece, as wide as the line; its other pieces are hidden.
          if (s.hidden) return null;
          const inEditedLine = props.editing?.page === props.index && props.editing.line.spans.includes(s.index);
          if (inEditedLine && props.editing!.span.index !== s.index) return null;
          const b = inEditedLine ? boxOfAll(props.editing!.line.spans.map((i) => spans[i])) : boxOf(s);
          if (inEditedLine) return <LineEditor key={s.index} box={b} editing={props.editing!} disabled={props.disabled} setEditing={props.setEditing} onSubmit={props.onSubmit} />;
          const selected = selectedSpans.has(s.index);
          const previewed = previewSpans.get(s.index);
          // A zero-size anchor at the page origin; its children are the span's areas (clicks on them reach the button).
          return (
            <button
              className={marquee ? 'obj no-hover' : 'obj'}
              key={s.index}
              data-testid={`span-${props.index}-${s.index}`}
              data-text={s.text}
              data-selected={selected ? 'true' : undefined}
              data-preview={previewed}
              data-glyphs={DEBUG_OVERLAY ? glyphsOf(s) : undefined}
              data-ocr={s.ocr ? JSON.stringify({ ...s.ocr, font: s.fontName }) : undefined}
              title={s.locked ? `${s.text} (cannot be changed: ${s.locked})` : s.ocr ? `Select "${s.text}" (recognised, ${Math.round(s.ocr.confidence)} % sure)` : `Select "${s.text}"`}
              disabled={props.disabled || !!s.locked}
              onClick={(e) => pick(lineItem(s), e)}
              onDoubleClick={(e) => editLine(s, e)}
              onPointerDown={(e) => selected && startDrag(e)}
              style={{ cursor: s.locked ? 'not-allowed' : selected && !props.readOnly ? 'move' : 'text', ...dragStyle(selected) }}
            >
              {segmentsOf(s).map((seg, k) => (
                <span key={k} className={s.ocr && s.ocr.confidence < UNCERTAIN ? `${markOf(selected, previewed)} is-uncertain` : markOf(selected, previewed)} style={seg} />
              ))}
            </button>
          );
        })}
      {toCss && fields.map((f) => <FieldArea key={f.id} page={props.index} field={f} box={fieldBox(f)} disabled={props.disabled} onFill={props.onFill} onSign={props.onSign} />)}
      {toCss && lone && !lone.locked && !props.disabled && !props.readOnly && !drag && !turning &&
        lone.quad.map(([x, y], k) => {
          const [cx, cy] = toCss(x, y);
          return <span key={`h${k}`} data-testid={`resize-${k}`} className="resize-handle" title="Drag to resize (Shift: free)" style={{ left: cx, top: cy }} onPointerDown={(e) => { if (e.button !== 0) return; e.preventDefault(); e.stopPropagation(); setResizing({ corner: k }); }} />;
        })}
      {toCss && props.selection?.page === props.index && !props.editing && !props.disabled && !props.readOnly && !drag && !turning && !resizing && !selLocked && turnGrips(props.selection)}
      {toCss && !!props.found?.length && (
        // Find's marks: a box around each match's glyphs (as seen), over the page; on screen only, never in the PDF.
        <svg className="find-marks" data-testid={`find-marks-${props.index}`} aria-hidden="true">
          {props.found.map((f, k) => {
            const pts = f.quads.flat().map(([x, y]) => toCss(x, y));
            const [x0, x1] = [Math.min(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[0]))];
            const [y0, y1] = [Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[1]))];
            return <rect key={k} ref={f.current ? currentMark : undefined} data-testid="find-mark" data-current={f.current || undefined} className={f.current ? 'find-mark is-current' : 'find-mark'} x={x0 - 1} y={y0 - 1} width={x1 - x0 + 2} height={y1 - y0 + 2} rx={2} />;
          })}
        </svg>
      )}
      {toCss && resizing?.T && lone && (
        <svg className="resize-preview" data-testid="resize-preview">
          <polygon points={lone.quad.map(([x, y]) => { const [u, v] = [resizing.T![0] * x + resizing.T![2] * y + resizing.T![4], resizing.T![1] * x + resizing.T![3] * y + resizing.T![5]]; return toCss(u, v).join(','); }).join(' ')} />
        </svg>
      )}
      {toCss && props.selection?.page === props.index && !props.editing && !drag && !turning && !resizing && <SelectionToolbar {...props} selection={props.selection} at={toolbarAt(props.selection)} pageWidth={props.size.width} startDrag={startDrag} />}
    </>
  );
}

/**
 * The editor over a line being edited (AC-UX-3): a text field exactly over the line, at its size on screen, in a
 * font like the PDF's. Enter or a click elsewhere applies; Escape cancels. Leaving the window does not apply.
 */
function LineEditor(props: { box: { left: number; top: number; width: number; height: number }; editing: Editing; disabled: boolean; setEditing: (e?: Editing) => void; onSubmit: () => void }) {
  const { box: b, editing } = props;
  const input = useRef<HTMLInputElement>(null);
  /** Escape or Enter was pressed: a blur that follows (the field going away) must not apply again */
  const settled = useRef(false);
  const font = cssFontFor(editing.span.fontName);
  // Glyph boxes span the font's whole height (about 1.2 em): the text size is a little under the box height.
  const size = Math.max(8, b.height / 1.2);
  const grow = Math.max(0, editing.text.length - editing.line.text.length);
  useLayoutEffect(() => {
    const el = input.current!;
    el.focus();
    const [a, z] = editing.select ?? [el.value.length, el.value.length];
    el.setSelectionRange(a, z);
  }, []);
  return (
    <form
      className="line-editor"
      style={{ left: b.left - 4, top: b.top - 3 }}
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit();
      }}
    >
      <input
        ref={input}
        data-testid="edit-input"
        aria-label="Edit the line's text"
        value={editing.text}
        readOnly={props.disabled}
        spellCheck={false}
        style={{ height: b.height + 6, minWidth: Math.max(60, b.width + 12), width: `calc(${Math.max(60, b.width + 12)}px + ${grow + 1}ch)`, fontFamily: font.family, fontWeight: font.weight, fontStyle: font.style, fontSize: size, lineHeight: `${b.height}px` }}
        onChange={(e) => {
          settled.current = false;
          props.setEditing({ ...editing, text: e.target.value, select: undefined });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            settled.current = true;
            props.setEditing(undefined);
          } else if (e.key === 'Enter') settled.current = true;
        }}
        onBlur={() => {
          // A click elsewhere applies; switching to another window, or a question about the edit being open, does not.
          if (!settled.current && !props.disabled && input.current?.isConnected && document.hasFocus()) props.onSubmit();
        }}
      />
    </form>
  );
}
